import { z } from 'zod';

import { REASON_CODES, type ReasonCode } from '../reason-code';

/** What an enum value unknown to this Mini App version reads as after parsing. */
export const UNKNOWN_VALUE = 'UNKNOWN';

/**
 * OpenAPI vendor extension on a field the Mini App parses leniently (C1 R10). The document still
 * lists the closed set of values the API must send; the marker only says that this client keeps
 * working, with a neutral rendering, if a newer API sends a value outside it.
 */
export const CLIENT_TOLERATES_UNKNOWN = 'x-fxmate-client-tolerates-unknown';

/**
 * Producer view of each lenient schema: the closed values the API must emit, and the sentence
 * that tells the API side how this client reads anything else. `buildOpenApiDocument` renders
 * these instead of the lenient zod shape (ADR 0003: the pinned document describes what the API
 * sends; C1 R10: the Mini App tolerates more). Proposal, to be validated with Sofien.
 */
interface ProducerView {
  values: readonly string[];
  description: string;
}

const PRODUCER_VIEWS = new WeakMap<object, ProducerView>();

/** The closed producer view of a lenient response schema, or `undefined` for any other schema. */
export function producerViewOf(schema: object): ProducerView | undefined {
  return PRODUCER_VIEWS.get(schema);
}

/**
 * A response enum that stays forward compatible (C1 R10): the known values parse as themselves,
 * and any other non-empty string (a value a newer API added) parses as `'UNKNOWN'`, so one new
 * value never makes a whole response fail. The screen shows a neutral line for `'UNKNOWN'`.
 *
 * Only for response fields whose unknown value has a decided neutral rendering. A field where an
 * unknown value has no agreed meaning (e.g. a position `status`) keeps a closed `z.enum`.
 * In `openapi.json` it reads as the closed `enum` the API must send, marked
 * `x-fxmate-client-tolerates-unknown: true` (producer view, see `PRODUCER_VIEWS`).
 *
 * Never call `.describe()`, `.meta()` or `.brand()` on the result: zod v4 returns a new
 * instance, which has no producer view, so `openapi.json` would fall back to `anyOf`
 * (`build-document.test.ts` fails if it does). Wrapping it (`.nullable()`, `z.array()`) is fine.
 *
 * @example openEnum(['EXECUTED', 'FAILED']).parse('PENDING') // 'UNKNOWN'
 */
export function openEnum<const T extends readonly [string, ...string[]]>(values: T) {
  const schema = z.enum(values).or(
    z
      .string()
      .min(1)
      .max(64)
      .transform((): typeof UNKNOWN_VALUE => UNKNOWN_VALUE),
  );
  PRODUCER_VIEWS.set(schema, {
    values,
    description:
      'The API sends only these values. A newer value is parsed by the Mini App as UNKNOWN and ' +
      'shown as a neutral line (C1 R10).',
  });
  return schema;
}

/**
 * A reason code in a response field (a warning or an execution failure), kept as a string: an
 * unknown code is not a parse failure, the screen routes it through `reasonMessage`, which shows
 * the generic C2 text (C2 R7). Never displayed raw (I12). The pattern still rejects prose (a raw
 * broker message is never a code). Unlike `Problem.reasonCode`, which stays closed (C1 R10).
 *
 * `values` is the producer view: the codes the API may send in this field, written as the closed
 * `enum` of `openapi.json`. Pass the field's own subset (warnings: `STOP_LOSS_REFUSED_BY_BROKER`);
 * the whole C2 catalogue is the default only for a field that may carry any code
 * (`EXECUTION.reasonCode`). The parse itself accepts any code of the right shape either way.
 *
 * Never call `.describe()`, `.meta()` or `.brand()` on the result: zod v4 returns a new
 * instance, which has no producer view, so `openapi.json` would fall back to the lenient shape
 * (`build-document.test.ts` fails if it does). Wrapping it (`.nullable()`, `z.array()`) is fine.
 *
 * @example openReasonCode(['STOP_LOSS_REFUSED_BY_BROKER']).parse('NEW_WARNING') // 'NEW_WARNING'
 */
export function openReasonCode(values: readonly ReasonCode[] = REASON_CODES) {
  const schema = z
    .string()
    .max(64)
    .regex(/^[A-Z][A-Z0-9_]*$/);
  PRODUCER_VIEWS.set(schema, {
    values,
    description:
      'A C2 reason code; the API sends only these. A code unknown to the Mini App is kept and ' +
      'shown with the generic C2 text, never raw (C1 R10, C2 R7, I12).',
  });
  return schema;
}
